import {
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Tooltip,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useEffect, useState } from "react";
import InfoIcon from "@mui/icons-material/Info";
import Header from "../../../components/Header";
import { setupManagementUrl, loanManagementUrl } from "../../../config";
import {
  executeDynamicSubmit,
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../data/DynamicServiceNew/jsondynamicservice";
import {
  allProductChargesAccounts,
  ProductRepaymentType,
} from "../../../data/Axios/queries";
import Swal from "sweetalert2";
import axios from "axios";
import { NumericFormat } from "react-number-format";

const setup_url = setupManagementUrl.uri;

const Form = (props) => {
  const ProductId = localStorage.getItem("ProductId");
  const [accountSubcategories, setAccountSubcategories] = useState([]); // State for account
  const [repaymentType, setRepaymentType] = useState([]);
  const [isAssetFinanceEnabled, setIsAssetFinanceEnabled] = useState(false);
  const [productTotalInstallments, setProductTotalInstallments] = useState(null);
  const [isProductAssetFinance, setIsProductAssetFinance] = useState(false);

  // Fetch the product's configured number of installments, so the expense
  // recovery period can be validated against it.
  useEffect(() => {
    const fetchProductInstallments = async () => {
      try {
        const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
        const response = await fetchJsonDynamic(
          "allProductsByColumnValues",
          "parameters",
          { id: parseInt(ProductId) },
          setup_url
        );
        const parsedResponse = response?.results ? JSON.parse(response.results) : null;
        const product = parsedResponse?.[0];

        if (product) {
          const total = product.IsdynamicInstallment
            ? product.maximumTerm
            : product.NoOfInstallments;
          setProductTotalInstallments(total ? Number(total) : null);
          setIsProductAssetFinance(!!product.IsSecuredproduct);
        }
      } catch (error) {
        console.error("Error fetching product installments:", error);
      }
    };
    fetchProductInstallments();
  }, []);

  // Fetch loan settings to determine if Asset Finance is enabled
  useEffect(() => {
    const fetchLoanSettings = async () => {
      try {
        const response = await fetchJsonDynamic(
          "loanSettings",
          null,
          null,
          loanManagementUrl.uri
        );
        if (response?.results) {
          const settings = JSON.parse(response.results);
          const assetFinanceSetting = settings.find(
            (s) => s.Setting === "Is Asset Finance"
          );
          setIsAssetFinanceEnabled(
            assetFinanceSetting?.Value?.toLowerCase() === "true"
          );
        }
      } catch (error) {
        console.error("Error fetching loan settings:", error);
        setIsAssetFinanceEnabled(false);
      }
    };
    fetchLoanSettings();
  }, []);

  // Fetch account subcategories on component mount
  useEffect(() => {
    const fetchAccountSubcategories = async () => {
      const token = localStorage.getItem("token");
      const formattedToken = `Bearer ${token?.replace(/"/g, "")}`;
      try {
        const response = await axios.post(
          setup_url,
          allProductChargesAccounts,
          {
            headers: {
              Authorization: formattedToken,
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Full Response:", response);

        // Ensure the results are correctly accessed from data.accountSubCategory.results
        const resultsString =
          response?.data?.data?.productChargesAccount?.results;

        if (resultsString) {
          // Parse the results string into a JavaScript array
          const accountData = JSON.parse(resultsString);
          console.log("Account Data:", accountData);

          // Set the account data to state if it's valid
          setAccountSubcategories(accountData);
        } else {
          console.error("Results field is not found or is empty.");
        }
      } catch (error) {
        console.error("Error fetching account subcategories:", error);
      }
    };

    fetchAccountSubcategories();
  }, []);

  useEffect(() => {
    const fetchRepaymentTypes = async () => {
      const token = localStorage.getItem("token");
      const formattedToken = `Bearer ${token?.replace(/"/g, "")}`;
      try {
        const response = await axios.post(setup_url, ProductRepaymentType, {
          headers: {
            Authorization: formattedToken,
            "Content-Type": "application/json",
          },
        });

        console.log("Full Response:", response);

        // Ensure the results are correctly accessed from data.accountSubCategory.results
        const resultsString =
          response?.data?.data?.productRepaymentType?.results;

        if (resultsString) {
          // Parse the results string into a JavaScript array
          const typeData = JSON.parse(resultsString);
          console.log("Account Data:", typeData);

          // Set the account data to state if it's valid
          setRepaymentType(typeData);
        } else {
          console.error("Results field is not found or is empty.");
        }
      } catch (error) {
        console.error("Error fetching account subcategories:", error);
      }
    };

    fetchRepaymentTypes();
  }, []);

  // Expense/installment charge options only make sense for products that are
  // themselves flagged as asset finance products, on top of the company-wide
  // "Is Asset Finance" setting.
  const showAssetFinanceFields = isAssetFinanceEnabled && isProductAssetFinance;

  const initialValues = {
    chargeName: props.data ? props.data.ChargeName : "",
    chargeAmount: props.data ? props.data.ChargeAmount : "",
    accountId: props.data ? props.data.AccountId || "" : "", // Add accountId in
    repaymentTypeId: props.data ? props.data.RepaymentTypeId || "" : "",
    isPercentage: props.data ? props.data.IsPercentage : false,
    isAddedToInstallments: props.data
      ? !!props.data.IsAddedToInstallments
      : false,
    hasExpenses: props.data ? !!props.data.HasExpenses : false,
    expenseAmount: props.data ? (props.data.ExpenseAmount ?? "") : "",
    expenseRecoveryInstallments: props.data
      ? (props.data.ExpenseRecoveryInstallments ?? "")
      : "",
    isExpense: props.data ? !!props.data.IsExpense : false,
    isUpfrontDynamicExpense: props.data
      ? !!props.data.IsUpfrontDynamicExpense
      : false,
    amountPerInstallment: props.data
      ? (props.data.AmountPerInstallment ?? "")
      : "",
  };

  const upfrontTotalCharge = (amountPerInstallment, installments) =>
    (parseFloat(amountPerInstallment) || 0) * (Number(installments) || 0);

  const installmentsLimitTest = (value) =>
    !productTotalInstallments ||
    value === undefined ||
    value === null ||
    value <= productTotalInstallments;
  const installmentsLimitMessage = productTotalInstallments
    ? `Cannot exceed the product's ${productTotalInstallments} installments`
    : "Cannot exceed the product's total installments";

  const isFullyRecovered = (hasExpenses, expenseRecoveryInstallments) =>
    !!hasExpenses &&
    !!productTotalInstallments &&
    Number(expenseRecoveryInstallments) === Number(productTotalInstallments);

  const validationSchema = yup.object().shape({
    chargeName: yup.string().required("Charge Name required"),
    chargeAmount: yup
      .number()
      .nullable()
      .when(
        ["hasExpenses", "expenseRecoveryInstallments", "isUpfrontDynamicExpense"],
        {
        is: (hasExpenses, installments, isUpfront) =>
          isUpfront || isFullyRecovered(hasExpenses, installments),
        then: (s) => s.nullable(),
        otherwise: (s) =>
          s
            .typeError("Charge Amount is required")
            .required("Charge Amount required")
            .positive("Charge Amount must be a positive number")
            .test(
              "percentage-range",
              "Percentage must be between 0 and 100",
              function (value) {
                const { isPercentage } = this.parent;

                if (!isPercentage) return true;
                return value >= 0 && value <= 100;
              }
            ),
        }
      ),
    accountId: yup.string().required("Account selection required"), // Add validation for account
    repaymentTypeId: yup.string().required("Please select repayment Method"),
    expenseAmount: yup
      .number()
      .nullable()
      .when("hasExpenses", {
        is: true,
        then: yup
          .number()
          .typeError("Expense Amount is required")
          .required("Expense Amount required")
          .positive("Expense Amount must be a positive number"),
        otherwise: yup.number().nullable(),
      }),
    amountPerInstallment: yup
      .number()
      .nullable()
      .when("isUpfrontDynamicExpense", {
        is: true,
        then: yup
          .number()
          .typeError("Amount per installment is required")
          .required("Amount per installment required")
          .positive("Amount per installment must be a positive number"),
        otherwise: yup.number().nullable(),
      }),
    expenseRecoveryInstallments: yup
      .number()
      .nullable()
      .when("hasExpenses", {
        is: true,
        then: yup
          .number()
          .typeError("Enter the number of installments")
          .required("Number of installments is required")
          .integer("Must be a whole number of installments")
          .min(1, "Must be at least 1 installment")
          .test(
            "within-total-installments",
            installmentsLimitMessage,
            installmentsLimitTest
          ),
        otherwise: yup.number().nullable(),
      }),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    const {
      chargeName,
      chargeAmount,
      accountId,
      repaymentTypeId,
      isPercentage,
      isAddedToInstallments,
      hasExpenses,
      expenseAmount,
      expenseRecoveryInstallments,
      isExpense,
      isUpfrontDynamicExpense,
      amountPerInstallment,
    } = values;
    const productId = parseInt(ProductId);
    const appliesToInstallments = showAssetFinanceFields && isAddedToInstallments;
    const appliesUpfront = showAssetFinanceFields && isUpfrontDynamicExpense;
    const appliesExpenses = appliesToInstallments && hasExpenses && !appliesUpfront;
    const appliesIsExpense = showAssetFinanceFields && !isAddedToInstallments && isExpense;
    const fullyRecovered = isFullyRecovered(
      hasExpenses,
      expenseRecoveryInstallments
    );
    const remainingInstallments =
      appliesExpenses &&
      productTotalInstallments &&
      expenseRecoveryInstallments !== "" &&
      expenseRecoveryInstallments != null
        ? Math.max(
            productTotalInstallments - Number(expenseRecoveryInstallments),
            0
          )
        : null;
    const totalIncome =
      remainingInstallments !== null
        ? remainingInstallments * (parseFloat(chargeAmount) || 0)
        : null;
    const commonFields = {
      chargeName,
      chargeAmount: appliesUpfront
        ? upfrontTotalCharge(amountPerInstallment, productTotalInstallments)
        : fullyRecovered
          ? 0
          : chargeAmount,
      productId,
      accountId,
      repaymentTypeId,
      isPercentage,
      isAddedToInstallments: appliesToInstallments,
      hasExpenses: appliesExpenses,
      expenseAmount: appliesExpenses ? expenseAmount : null,
      expenseRecoveryInstallments:
        appliesExpenses
          ? parseInt(expenseRecoveryInstallments)
          : appliesUpfront
            ? productTotalInstallments
            : null,
      isUpfrontDynamicExpense: !!appliesUpfront,
      amountPerInstallment: appliesUpfront
        ? parseFloat(amountPerInstallment)
        : null,
      isExpense: appliesIsExpense,
      totalIncome,
    };
    const variables = props.isEditing
      ? {
          id: props.data.Id,
          ...commonFields,
        }
      : commonFields;

    await executeDynamicSubmit({
      mutationName: props.isEditing ? "updateProductCharge" : "createProductCharge",
      variables,
      mutationObjectName: "result",
      baseUrl: setup_url,
      isEditing: props.isEditing,
      entityName: "Product Charge",
      onSuccess: () => {
        props.onClose();
        props.onAction();
      },
      setSubmitting,
    });
  };

  return (
    <Box m="20px">
      <Header
        title={
          props.isEditing ? "Edit Product Charge" : "Create Product Charge"
        }
        subtitle={
          props.isEditing
            ? "Edit an Existing Product Charge"
            : "Create a New Product Charge"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="30px">
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Product Charge"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.chargeName}
                name="chargeName"
                error={touched.chargeName && !!errors.chargeName}
                helperText={touched.chargeName && errors.chargeName}
                sx={{ gridColumn: "span 2" }}
              />
              {showAssetFinanceFields && (() => {
                const fullyRecovered = isFullyRecovered(
                  values.hasExpenses,
                  values.expenseRecoveryInstallments
                );
                return (
                  <Box
                    sx={{
                      gridColumn: "span 2",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.03)"
                          : "rgba(25,118,210,0.04)",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        color: "text.secondary",
                        mb: 1,
                      }}
                    >
                      Asset Finance Charge Settings
                    </Box>

                    <Box
                      display="flex"
                      flexWrap="wrap"
                      alignItems="center"
                      columnGap={3}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.isAddedToInstallments}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFieldValue("isAddedToInstallments", checked);
                              if (checked) {
                                // Mutually exclusive with the "whole charge is
                                // an expense" flag below.
                                setFieldValue("isExpense", false);
                              } else {
                                setFieldValue("hasExpenses", false);
                                setFieldValue("expenseAmount", "");
                                if (!values.isUpfrontDynamicExpense) {
                                  setFieldValue("expenseRecoveryInstallments", "");
                                }
                              }
                            }}
                            name="isAddedToInstallments"
                          />
                        }
                        label={
                          <Box display="flex" alignItems="center">
                            Is Added To Installments
                            <Tooltip title="Check if this charge should be added to the installments">
                              <IconButton size="small">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }
                      />

                      {values.isAddedToInstallments ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.hasExpenses}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFieldValue("hasExpenses", checked);
                                if (checked) {
                                  // Mutually exclusive with Upfront Dynamic Expense.
                                  setFieldValue("isUpfrontDynamicExpense", false);
                                  setFieldValue("amountPerInstallment", "");
                                } else {
                                  setFieldValue("expenseAmount", "");
                                }
                                setFieldValue(
                                  "expenseRecoveryInstallments",
                                  ""
                                );
                              }}
                              name="hasExpenses"
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center">
                              Has Expenses
                              <Tooltip title="Check if this charge has associated expenses to recover from the customer over a number of installments">
                                <IconButton size="small">
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                        />
                      ) : (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.isExpense}
                              onChange={(e) =>
                                setFieldValue("isExpense", e.target.checked)
                              }
                              name="isExpense"
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center">
                              Is Expense
                              <Tooltip title="Check if the whole Charge Amount is an expense rather than income (used when this charge is not added to the installment schedule)">
                                <IconButton size="small">
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                        />
                      )}

                      {(
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.isUpfrontDynamicExpense}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFieldValue("isUpfrontDynamicExpense", checked);
                                // Shares expenseRecoveryInstallments with
                                // Has Expenses, so reset it on switch.
                                setFieldValue("expenseRecoveryInstallments", "");
                                if (checked) {
                                  setFieldValue("hasExpenses", false);
                                  setFieldValue("expenseAmount", "");
                                } else {
                                  setFieldValue("amountPerInstallment", "");
                                }
                              }}
                              name="isUpfrontDynamicExpense"
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center">
                              Is Upfront Dynamic Expense
                              <Tooltip title="Charge a fixed amount per installment for a chosen number of installments. This charge is purely an expense, not income. Cannot be combined with Has Expenses">
                                <IconButton size="small">
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                        />
                      )}
                    </Box>

                    {values.isUpfrontDynamicExpense && (
                        <Box
                          sx={{ mt: 1 }}
                          display="grid"
                          gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, 1fr)" }}
                          gap={2}
                        >
                          <NumericFormat
                            customInput={TextField}
                            fullWidth
                            variant="filled"
                            label="Amount per Installment"
                            name="amountPerInstallment"
                            value={values.amountPerInstallment}
                            thousandSeparator
                            decimalScale={2}
                            fixedDecimalScale
                            allowNegative={false}
                            onValueChange={({ floatValue }) => {
                              setFieldValue(
                                "amountPerInstallment",
                                floatValue === undefined ? "" : floatValue
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.amountPerInstallment &&
                              !!errors.amountPerInstallment
                            }
                            helperText={
                              (touched.amountPerInstallment &&
                                errors.amountPerInstallment) ||
                              " "
                            }
                          />
                          {/* Number of installments is no longer entered for
                              upfront dynamic expenses; the charge uses the
                              product's max installments instead.
                          <NumericFormat
                            customInput={TextField}
                            fullWidth
                            variant="filled"
                            label="Number of Installments"
                            name="expenseRecoveryInstallments"
                            value={values.expenseRecoveryInstallments}
                            decimalScale={0}
                            allowNegative={false}
                            isAllowed={({ floatValue }) =>
                              floatValue === undefined ||
                              !productTotalInstallments ||
                              floatValue <= productTotalInstallments
                            }
                            onValueChange={({ floatValue }) => {
                              setFieldValue(
                                "expenseRecoveryInstallments",
                                floatValue === undefined ? "" : floatValue
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.expenseRecoveryInstallments &&
                              !!errors.expenseRecoveryInstallments
                            }
                            helperText={
                              (touched.expenseRecoveryInstallments &&
                                errors.expenseRecoveryInstallments) ||
                              (productTotalInstallments
                                ? `Max ${productTotalInstallments} installments`
                                : " ")
                            }
                          />
                          */}
                        </Box>
                      )}

                    {values.isAddedToInstallments && values.hasExpenses && (() => {
                      const recoveryInstallments =
                        values.expenseRecoveryInstallments !== "" &&
                        values.expenseRecoveryInstallments != null
                          ? Number(values.expenseRecoveryInstallments)
                          : null;
                      const remainingInstallments =
                        productTotalInstallments && recoveryInstallments !== null
                          ? Math.max(
                              productTotalInstallments - recoveryInstallments,
                              0
                            )
                          : null;
                      const totalIncome =
                        remainingInstallments !== null
                          ? remainingInstallments *
                            (parseFloat(values.chargeAmount) || 0)
                          : 0;

                      return (
                      <Box sx={{ mt: 1 }}>
                        {productTotalInstallments && (
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                              mb: 1.5,
                              fontSize: "0.8rem",
                              color: "text.secondary",
                            }}
                          >
                            <InfoIcon sx={{ fontSize: 16 }} />
                            {remainingInstallments !== null ? (
                              <>
                                This product runs for{" "}
                                <strong>{productTotalInstallments}</strong>{" "}
                                installments — {recoveryInstallments} will
                                recover the expense, leaving{" "}
                                <strong>{remainingInstallments}</strong> to
                                carry the flat Charge Amount as income.
                              </>
                            ) : (
                              <>
                                This product runs for{" "}
                                <strong>{productTotalInstallments}</strong>{" "}
                                installments in total — choose how many of
                                those should be used to recover the expense.
                              </>
                            )}
                          </Box>
                        )}

                        <Box
                          display="grid"
                          gridTemplateColumns={{
                            xs: "1fr",
                            sm: "repeat(4, 1fr)",
                          }}
                          gap={2}
                        >
                          <NumericFormat
                            customInput={TextField}
                            fullWidth
                            variant="filled"
                            label="Expense Amount"
                            name="expenseAmount"
                            value={values.expenseAmount}
                            thousandSeparator
                            decimalScale={2}
                            fixedDecimalScale
                            allowNegative={false}
                            onValueChange={({ floatValue }) => {
                              setFieldValue(
                                "expenseAmount",
                                floatValue === undefined ? "" : floatValue
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.expenseAmount && !!errors.expenseAmount
                            }
                            helperText={
                              touched.expenseAmount && errors.expenseAmount
                            }
                          />
                          <NumericFormat
                            customInput={TextField}
                            fullWidth
                            variant="filled"
                            label="Recover Expense Over (Installments)"
                            name="expenseRecoveryInstallments"
                            value={values.expenseRecoveryInstallments}
                            decimalScale={0}
                            allowNegative={false}
                            isAllowed={({ floatValue }) =>
                              floatValue === undefined ||
                              !productTotalInstallments ||
                              floatValue <= productTotalInstallments
                            }
                            onValueChange={({ floatValue }) => {
                              setFieldValue(
                                "expenseRecoveryInstallments",
                                floatValue === undefined ? "" : floatValue
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.expenseRecoveryInstallments &&
                              !!errors.expenseRecoveryInstallments
                            }
                            helperText={
                              (touched.expenseRecoveryInstallments &&
                                errors.expenseRecoveryInstallments) ||
                              (productTotalInstallments
                                ? `Max ${productTotalInstallments} installments`
                                : " ")
                            }
                          />
                          <NumericFormat
                            customInput={TextField}
                            fullWidth
                            variant="filled"
                            label="Expense Amount per Installment"
                            value={
                              values.expenseRecoveryInstallments &&
                              Number(values.expenseRecoveryInstallments) > 0
                                ? (parseFloat(values.expenseAmount) || 0) /
                                  Number(values.expenseRecoveryInstallments)
                                : 0
                            }
                            thousandSeparator
                            decimalScale={2}
                            fixedDecimalScale
                            InputProps={{ readOnly: true }}
                            helperText="Preview only — not saved"
                          />
                          <NumericFormat
                            customInput={TextField}
                            fullWidth
                            variant="filled"
                            disabled
                            label="Total Income (Remaining Installments)"
                            value={totalIncome}
                            thousandSeparator
                            decimalScale={2}
                            fixedDecimalScale
                            helperText={
                              remainingInstallments !== null
                                ? `${remainingInstallments} installment${remainingInstallments === 1 ? "" : "s"} × Charge Amount`
                                : "Enter recovery installments to preview"
                            }
                          />
                        </Box>

                        {fullyRecovered && (
                          <Box
                            sx={{
                              mt: 1.5,
                              p: 1.25,
                              borderRadius: 1,
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255,167,38,0.12)"
                                  : "rgba(255,167,38,0.15)",
                              color: "warning.dark",
                              fontSize: "0.8rem",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <InfoIcon sx={{ fontSize: 16 }} />
                            Recovery covers all {productTotalInstallments}{" "}
                            installments, so there are no remaining
                            installments to apply the flat Charge Amount to —
                            it has been disabled below.
                          </Box>
                        )}
                      </Box>
                      );
                    })()}
                  </Box>
                );
              })()}
              <NumericFormat
                customInput={TextField}
                fullWidth
                variant="filled"
                disabled={
                  (showAssetFinanceFields && values.isUpfrontDynamicExpense) ||
                  isFullyRecovered(
                    values.hasExpenses,
                    values.expenseRecoveryInstallments
                  )
                }
                label={
                  values.isPercentage
                    ? "Charge Percentage"
                    : showAssetFinanceFields && values.isUpfrontDynamicExpense
                    ? "Charge Amount (Expense)"
                    : values.isAddedToInstallments && values.hasExpenses
                      ? "Charge Amount (Flat Income After Recovery)"
                      : !values.isAddedToInstallments && values.isExpense
                        ? "Charge Amount (Expense)"
                        : "Charge Amount"
                }
                name="chargeAmount"
                value={
                  showAssetFinanceFields && values.isUpfrontDynamicExpense
                    ? upfrontTotalCharge(
                        values.amountPerInstallment,
                        productTotalInstallments
                      )
                    : values.chargeAmount
                }
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                suffix={values.isPercentage ? "%" : ""}
                onValueChange={({ floatValue }) => {
                  setFieldValue(
                    "chargeAmount",
                    floatValue === undefined ? "" : floatValue
                  );
                }}
                onBlur={handleBlur}
                error={touched.chargeAmount && !!errors.chargeAmount}
                helperText={
                  (touched.chargeAmount && errors.chargeAmount) ||
                  (showAssetFinanceFields && values.isUpfrontDynamicExpense
                    ? `Amount per installment × ${productTotalInstallments ?? "max"} installments (product's maximum). This charge is purely an expense, not income`
                    : isFullyRecovered(
                    values.hasExpenses,
                    values.expenseRecoveryInstallments
                  )
                    ? "Disabled — expense recovery covers every installment"
                    : values.isAddedToInstallments && values.hasExpenses
                      ? "Flat KES amount added to installments once the recovery period above has elapsed"
                      : !values.isAddedToInstallments && values.isExpense
                        ? "This entire amount is treated as an expense, not income"
                        : " ")
                }
                sx={{ gridColumn: "span 2" }}
              />
              <FormControl
                fullWidth
                variant="filled"
                sx={{ gridColumn: "span 2" }}
              >
                <InputLabel>Account</InputLabel>
                <Select
                  name="accountId"
                  value={values.accountId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.accountId && !!errors.accountId}
                >
                  {accountSubcategories.map((subcategory) => (
                    <MenuItem key={subcategory.Id} value={subcategory.Id}>
                      {subcategory.AccountName}{" "}
                      {/* Adjust based on subcategory object structure */}
                    </MenuItem>
                  ))}
                </Select>
                {touched.accountId && errors.accountId && (
                  <div style={{ color: "red", fontSize: "0.875rem" }}>
                    {errors.accountId}
                  </div>
                )}
              </FormControl>
              <FormControl
                fullWidth
                variant="filled"
                sx={{ gridColumn: "span 2" }}
              >
                <InputLabel>Repayment Type</InputLabel>
                <Select
                  name="repaymentTypeId"
                  value={values.repaymentTypeId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.repaymentTypeId && !!errors.repaymentTypeId}
                >
                  {repaymentType.map((type) => (
                    <MenuItem key={type.Id} value={type.Id}>
                      {type.Name}{" "}
                      {/* Adjust based on subcategory object structure */}
                    </MenuItem>
                  ))}
                </Select>
                {touched.repaymentTypeId && errors.repaymentTypeId && (
                  <div style={{ color: "red", fontSize: "0.875rem" }}>
                    {errors.repaymentTypeId}
                  </div>
                )}
              </FormControl>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.isPercentage}
                  onChange={(e) =>
                    setFieldValue("isPercentage", e.target.checked)
                  }
                  name="isPercentage"
                />
              }
              label={
                <Box display="flex" alignItems="center">
                  Is Percentage
                  <Tooltip title="Check if this product charge is Percentage">
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />

            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default Form;
